/**
 * Paid add-on services (settings group "Paid add-on services").
 *
 * Every service is its own cart line, linked to its main product through the
 * hidden line property `_addon_for` (the main product's handle). This file:
 *  - indents addon lines under their main product in the drawer and cart page,
 *  - keeps addon quantities in step when the MAIN line's quantity changes and
 *    removes addons whose main product is gone (orphan cleanup),
 *  - keeps the product page "Total with finishing" line live.
 *
 * Customer-initiated changes to an addon line itself (e.g. removing one
 * service) are never overwritten - only a main-line change cascades.
 */
(function () {
  'use strict';

  var ADDON_ATTR = 'data-kp-addon-for';
  var HANDLE_ATTR = 'data-kp-product-handle';
  var KEY_ATTR = 'data-kp-line-key';
  var CHILD_CLASS = 'kp-addon-child';
  var PARENT_CLASS = 'kp-addon-parent';

  var lastMainQuantities = null; // handle -> total qty across lines, from the last cart read
  var syncInFlight = false;
  var syncQueued = false;

  // ---------------------------------------------------------------- money

  function money(cents, format) {
    if (typeof cents !== 'number' || isNaN(cents)) return '';
    var value = cents / 100;
    var dec2 = value.toFixed(2);
    var groupedDec = dec2.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var noDec = String(Math.round(value));
    var grouped = noDec.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return (format || '{{amount}}')
      .replace('{{amount_no_decimals_with_comma_separator}}', grouped)
      .replace('{{amount_with_comma_separator}}', groupedDec)
      .replace('{{amount_no_decimals}}', noDec)
      .replace('{{amount}}', groupedDec);
  }

  // ---------------------------------------------------------------- totals

  function updateTotal(wrap) {
    var totalEl = wrap.querySelector('[data-kp-addons-total]');
    if (!totalEl) return;
    var boxes = wrap.querySelectorAll('[data-kp-addon-checkbox]:checked');
    if (!boxes.length) {
      totalEl.hidden = true;
      return;
    }
    var servicesTotal = 0;
    boxes.forEach(function (box) {
      var price = Number(box.getAttribute('data-kp-addon-price'));
      if (!isNaN(price)) servicesTotal += price;
    });
    var mainPrice = Number(wrap.getAttribute('data-kp-addon-main-price'));
    var amount = !isNaN(mainPrice) && mainPrice > 0 ? mainPrice + servicesTotal : servicesTotal;
    var amountEl = totalEl.querySelector('[data-kp-addons-total-amount]');
    if (amountEl) {
      amountEl.textContent = money(amount, wrap.getAttribute('data-kp-addon-money-format'));
    }
    totalEl.hidden = false;
  }

  function updateAllTotals() {
    document.querySelectorAll('[data-kp-addons]').forEach(updateTotal);
  }

  document.addEventListener('change', function (event) {
    var box = event.target.closest && event.target.closest('[data-kp-addon-checkbox]');
    if (!box) return;
    var wrap = box.closest('[data-kp-addons]');
    if (wrap) updateTotal(wrap);
  });

  // ---------------------------------------------------------------- grouping

  function regroup(root) {
    var rows = root.querySelectorAll('tr.cart-item');
    if (!rows.length) return;

    var mainRows = new Map();
    rows.forEach(function (row) {
      row.classList.remove(CHILD_CLASS, PARENT_CLASS);
      var handle = row.getAttribute(HANDLE_ATTR);
      if (handle && !row.hasAttribute(ADDON_ATTR) && !mainRows.has(handle)) {
        mainRows.set(handle, row);
      }
    });
    if (!mainRows.size) return;

    rows.forEach(function (row) {
      var forHandle = row.getAttribute(ADDON_ATTR);
      if (!forHandle) return;
      var main = mainRows.get(forHandle);
      if (!main || main === row) return;
      row.classList.add(CHILD_CLASS);
      main.classList.add(PARENT_CLASS);
      // Only move the row when it is not already sitting directly under its
      // main line - moving a node fires the observer again.
      if (main.nextElementSibling === row) return;
      var anchor = main;
      while (
        anchor.nextElementSibling &&
        anchor.nextElementSibling.classList.contains(CHILD_CLASS) &&
        anchor.nextElementSibling !== row
      ) {
        anchor = anchor.nextElementSibling;
      }
      anchor.after(row);
    });
  }

  function regroupAll() {
    regroup(document.getElementById('CartDrawer') || document);
    regroup(document.querySelector('cart-items') || document);
  }

  if ('MutationObserver' in window) {
    var groupScheduled = false;
    var observer = new MutationObserver(function () {
      if (groupScheduled) return;
      groupScheduled = true;
      requestAnimationFrame(function () {
        groupScheduled = false;
        regroupAll();
      });
    });
    var startObserving = function () {
      observer.observe(document.body, { childList: true, subtree: true });
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', startObserving, { once: true });
    } else {
      startObserving();
    }
  }

  // ---------------------------------------------------------------- sync

  function readCart() {
    return fetch(window.routes.cart_url + '.js', {
      headers: { Accept: 'application/json' },
    }).then(function (response) {
      return response.json();
    });
  }

  function mapMainQuantities(cart) {
    var mains = new Map();
    (cart.items || []).forEach(function (item) {
      if (item.properties && item.properties._addon_for) return;
      var handle = item.handle;
      if (!handle) return;
      mains.set(handle, (mains.get(handle) || 0) + item.quantity);
    });
    return mains;
  }

  function cartSections() {
    var element = document.querySelector('cart-drawer-items') || document.querySelector('cart-items');
    if (element && typeof element.getSectionsToRender === 'function') {
      return element.getSectionsToRender().map(function (section) {
        return section.id;
      });
    }
    return [];
  }

  function syncAddons() {
    if (syncInFlight) {
      syncQueued = true;
      return Promise.resolve();
    }
    syncInFlight = true;
    var previousMains = lastMainQuantities;
    return readCart()
      .then(function (cart) {
        var mains = mapMainQuantities(cart);
        var updates = {};
        var touched = false;

        (cart.items || []).forEach(function (item) {
          var forHandle = item.properties && item.properties._addon_for;
          if (!forHandle || !item.key) return;
          // Orphan: the main product is no longer in the cart.
          if (!mains.has(forHandle)) {
            updates[item.key] = 0;
            touched = true;
            return;
          }
          // Cascade only when the MAIN line's quantity actually changed since
          // the last cart read. A direct edit on the addon line stays.
          if (
            previousMains &&
            previousMains.has(forHandle) &&
            previousMains.get(forHandle) !== mains.get(forHandle) &&
            item.quantity !== mains.get(forHandle)
          ) {
            updates[item.key] = mains.get(forHandle);
            touched = true;
          }
        });

        lastMainQuantities = mains;

        var keys = Object.keys(updates);
        if (!touched || !keys.length) return null;

        var body = { updates: updates };
        var sections = cartSections();
        if (sections.length) {
          body.sections = sections;
          body.sections_url = window.location.pathname;
        }
        return fetch(window.routes.cart_update_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        })
          .then(function (response) {
            return response.json();
          })
          .then(function (data) {
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'kp-addon-sync', cartData: data });
            }
            return data;
          });
      })
      .catch(function (error) {
        console.warn('kp-addon-cart: sync failed', error);
      })
      .then(function (result) {
        syncInFlight = false;
        if (syncQueued) {
          syncQueued = false;
          return syncAddons();
        }
        return result;
      });
  }

  function refreshOnly() {
    return readCart()
      .then(function (cart) {
        lastMainQuantities = mapMainQuantities(cart);
      })
      .catch(function () {});
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    // A customer changed a quantity or removed a line from a cart surface.
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (event && event.source === 'kp-addon-sync') return;
      if (event && event.source === 'cart-items') {
        syncAddons();
      } else {
        // An add (product-form / direct add) or our own fix-up: addon
        // quantities were written correctly, just re-baseline.
        refreshOnly();
      }
    });

    // The shopper picked a different colour/size: re-base the total line.
    subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
      var price = event && event.data && event.data.variant ? event.data.variant.price : null;
      document.querySelectorAll('[data-kp-addons]').forEach(function (wrap) {
        if (price !== null && price !== undefined) {
          wrap.setAttribute('data-kp-addon-main-price', String(price));
        }
        updateTotal(wrap);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      function () {
        regroupAll();
        updateAllTotals();
        // Full sync on load too: with no baseline yet this can only remove
        // orphaned addons (e.g. the main line was removed on the cart page,
        // which reloads the page instead of publishing a cart event).
        syncAddons();
      },
      { once: true }
    );
  } else {
    regroupAll();
    updateAllTotals();
    syncAddons();
  }
})();
